import { createSelector } from '@reduxjs/toolkit';
import { EmptyCatalog } from '@/types/Catalog';
import { EmptyCoverLot } from '@/types/CoverLot';
import { GlobalState } from '@/redux/rootReducer';
import { safelyEscapeText } from '@/utils/safelyRender';
import { shouldShowLive } from '@/utils/live';
import clamp from 'lodash/clamp';
import ms from 'ms';

export const CATALOG_REDUX_STORE_TIME = ms('30m');

const stateSelector = (state: Partial<GlobalState>) => state.catalog;
const idSelector = (state: GlobalState, id) => id;
const secondIdSelector = (state: GlobalState, notused, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getIsCatalogLoaded = createSelector([loadedSelector, idSelector], (loaded, id) =>
    loaded.hasOwnProperty(id)
);

export const getCatalog = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || EmptyCatalog);
export const getCatalogsByIds = createSelector([byIdSelector, idSelector], (byId, ids) =>
    ids.map((catalogId) => byId[catalogId]).filter((catalog) => Boolean(catalog?.catalogId))
);

export const getCatalogIsPreview = createSelector(
    getCatalog,
    (catalog) => catalog?.publishDateTs === 0 && catalog.catalogStatus !== 'done'
);

export const getCatalogIsLAPayments = createSelector(getCatalog, (catalog) =>
    Boolean(catalog?.liveAuctioneersPayments)
);
export const getCatalogAchPayments = createSelector(getCatalog, (catalog) => Boolean(catalog?.achPayments));
export const getCatalogHasVideo = createSelector(getCatalog, (catalog) => Boolean(catalog?.hasVideo));
export const getCatalogPublishDateTs = createSelector(getCatalog, (catalog) => catalog?.publishDateTs || 0);

export const getCatalogCurrency = createSelector(getCatalog, (catalog) => catalog?.currency || 'usd');
export const getCatalogPaymentOptions = createSelector(getCatalog, (catalog) => catalog?.paymentOptions);
export const getCatalogBuyersPremium = createSelector(
    getCatalog,
    (catalog) => catalog?.buyersPremium || EmptyCatalog.buyersPremium
);
export const getCatalogIsSEOExcluded = createSelector(getCatalog, (catalog) => Boolean(catalog?.isSEOExcluded));
export const getCatalogIsTimed = createSelector(getCatalog, (catalog) => Boolean(catalog?.isTimed));
export const getCatalogAbsenteeBidders = createSelector(getCatalog, (catalog) => catalog?.absenteeBidders || 0);
export const getCatalogPaymentProvider = createSelector(getCatalog, (catalog) => catalog?.paymentProvider);
export const getCatalogTitle = createSelector(getCatalog, (catalog) => catalog?.title || '');
export const getCatalogDescription = createSelector(getCatalog, (catalog) => catalog?.description || '');
export const getCatalogCleanDescription = createSelector(getCatalog, (catalog) =>
    safelyEscapeText(catalog?.description || '')
);
export const getCatalogSellerId = createSelector(getCatalog, (catalog) => catalog?.sellerId);
export const getCatalogSellerName = createSelector(getCatalog, (catalog) => catalog?.sellerName || '');
export const getCatalogPreview = createSelector(getCatalog, (catalog) => catalog?.preview || '');
export const getCatalogStatus = createSelector(getCatalog, (catalog) => catalog?.catalogStatus);
export const getCatalogStartTime = createSelector(getCatalog, (catalog) => catalog?.saleStartTs);
export const getCatalogAddress = createSelector(getCatalog, (catalog) => catalog?.address);
export const getCatalogLotsListed = createSelector(getCatalog, (catalog) => catalog?.lotsListed || 0);
export const getCatalogIsCatalogOnly = createSelector(getCatalog, (catalog) => Boolean(catalog?.isCatalogOnly));
export const getCatalogIsAutopay = createSelector(getCatalog, (catalog) => Boolean(catalog?.isAutopay));
export const getCatalogIsInstantAutopay = createSelector(getCatalog, (catalog) => Boolean(catalog?.isInstantAutopay));
export const getCatalogCoverLots = createSelector(
    getCatalog,
    (catalog) => catalog?.coverLots || [EmptyCoverLot, EmptyCoverLot, EmptyCoverLot]
);
export const getCatalogPercentComplete = createSelector([getCatalog, secondIdSelector], (catalog, index) => {
    if (!Number.isNaN(index) && !Number.isNaN(catalog.lotsListed) && catalog.lotsListed) {
        return clamp(((index + 1) / catalog.lotsListed) * 100, 2.5, 100);
    }
    return 0;
});

export const getShouldShowLive = createSelector([byIdSelector, idSelector], (byId, id) => {
    if (byId[id]) {
        const { catalogStatus, isCatalogOnly, saleStartTs } = byId[id];
        return shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    }
    return false;
});

export const getAllCatalogs = createSelector([byIdSelector], (byId) => byId);

export const getCatalogIdsForSeller = createSelector([byIdSelector, idSelector], (byId, id) =>
    Object.keys(byId)
        .map((key) => byId[Number(key)])
        .filter((catalog) => catalog?.sellerId === id && catalog?.catalogStatus)
        .map((catalog) => catalog.catalogId)
);

export const whichCatalogsNeeded = createSelector(
    [byIdSelector, loadedSelector, loadingSelector, idSelector],
    (ids, loaded, loading, catalogIds) => {
        const needed: number[] = [];
        catalogIds.forEach((catalogId: number) => {
            if (catalogId) {
                const time = Date.now();
                const diff = time - loaded[catalogId];
                if (diff < CATALOG_REDUX_STORE_TIME) {
                    return false;
                }
                if (!loading.includes(catalogId)) {
                    needed.push(catalogId);
                }
            }
        });
        return needed;
    }
);
